import type { Content, ContentRelationshipField } from "@prismicio/client";
import type { GameSpec as GameSpecifications } from "@ui-kit/GameSpecs";

import { getLinkedDocument, getWebLink } from "@cms/utils";

export type { GameSpecifications };

export type SocialNetwork = GameSpecifications["main"]["socials"]["0"];
export type AgeRating = GameSpecifications["specifications"]["ageRating"];
export type GameCompany = GameSpecifications["specifications"]["publisher"];

export const getGameSocials = (game: Content.GameDocument): SocialNetwork[] => {
  return game.data.socials
    .map((social) => {
      const link = getWebLink(social.link);
      const network = getLinkedDocument<
        Content.SocialNetworkDocument,
        Content.SocialNetworkDocumentData
      >(social.network);

      if (!link || !network) {
        return undefined;
      }

      return {
        link: link.url || "",
        logo: network.data?.logo?.url || "",
        network: network.data?.logo?.alt || "",
      };
    })
    .filter((social): social is SocialNetwork => social !== undefined);
};

const getGameGenres = (game: Content.GameDocument): string[] => {
  return game.data.genres
    .map((field) => {
      const genre = getLinkedDocument<
        Content.GameGenreDocument,
        Content.GameGenreDocumentData
      >(field.genre);

      if (!genre?.data) {
        return "";
      }

      return genre.data.name;
    })
    .filter((name): name is string => name !== "");
};

const getAgeRating = (game: Content.GameDocument): AgeRating => {
  const ageRating = getLinkedDocument<
    Content.GameAgeRaitingDocument,
    Content.GameAgeRaitingDocumentData
  >(game.data.age_raiting);

  return {
    name: ageRating?.data?.name || "",
    logo: ageRating?.data?.logo.url || "",
    description: ageRating?.data?.description || "",
  };
};

const getGamePlatforms = (
  game: Content.GameDocument,
): GameSpecifications["specifications"]["platforms"] => {
  return game.data.platforms
    .map((field) => {
      const platform = getLinkedDocument<
        Content.GamePlatformDocument,
        Content.GamePlatformDocumentData
      >(field.platform);

      const link = getWebLink(field.link);

      if (!platform?.data || !link) {
        return [];
      }

      return [platform.data.name, link.url];
    })
    .filter((values): values is [string, string] => values.length > 0);
};

const getGameCompany = (field: ContentRelationshipField): GameCompany => {
  const company = getLinkedDocument<
    Content.GamesCompanyDocument,
    Content.GamesCompanyDocumentData
  >(field);

  if (!company?.data) {
    return { name: "", website: "" } as GameCompany;
  }

  const website = getWebLink(company.data.website);

  return {
    name: company.data.name || "",
    website: website.url || "",
  };
};

export const getGameSpecs = (
  game: Content.GameDocument,
): GameSpecifications => {
  const website = getWebLink(game.data.website);
  const GameSpecifications: GameSpecifications = {
    main: {
      logo: game.data.logo_intro.url || "",
      socials: getGameSocials(game),
      website: website.url || "",
    },
    specifications: {
      genres: getGameGenres(game),
      ageRating: getAgeRating(game),
      platforms: getGamePlatforms(game),
      publisher: getGameCompany(game.data.publisher),
      studio: getGameCompany(game.data.studio),
      releaseDate: game.data.release_date || "",
    },
  };

  return GameSpecifications;
};
